/**
 * Short timestamps for list rows (chats, alerts): time today, then "Yesterday",
 * weekday, day-month. Free of app services so the native app can use it too.
 */
import { formatTime } from './dateFormat';

/**
 * Whole local calendar days from `date` to `now`. Elapsed milliseconds are wrong
 * here: a message from 23:30 last night is "Yesterday" at 00:10, and a day is 23
 * or 25 hours long across a UK clock change.
 */
export const calendarDaysAgo = (date: Date, now: Date = new Date()): number =>
    Math.round(
        (Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) -
            Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())) /
            86400000,
    );

/**
 * Formats a timestamp for a chat list row.
 *
 * Rules (calendar-day diff, not raw ms, so it never flips near midnight):
 *   today      → "HH:mm" (24h)
 *   yesterday  → "Yesterday"
 *   ≤ 6 days   → "Mon" / "Tue" … (short weekday)
 *   same year  → "5 Jan"
 *   other year → "5 Jan 24"
 */
export const formatChatListTime = (value?: string | Date | null): string => {
    if (!value) return '';
    const date = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(date.getTime())) return '';

    const now = new Date();
    const diffDays = calendarDaysAgo(date, now);

    if (diffDays === 0) {
        // HH:mm in 24-hour format (UK convention)
        return formatTime(date);
    }
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) {
        return date.toLocaleDateString([], { weekday: 'short' });
    }
    if (date.getFullYear() !== now.getFullYear()) {
        return date.toLocaleDateString([], { day: 'numeric', month: 'short', year: '2-digit' });
    }
    return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
};
